// The Synty low-poly kit for the hex field. Same pieces, palettes and night-glow map the Capital uses
// (public/models/capital), assembled into one-hex buildings from public/models/capital/hex_recipes.json.
//
//   buildRecipeGroup(id, seed)  → a THREE.Group sized for one hex (world units), ground top at y = 0
//   buildGroundCover(tiles)     → instanced undeveloped-land cover for plain tiles (one draw per piece type)
//   setSyntyNight(n)            → window/lantern glow, 0 (day) … 1 (night)
//   setBurned(wrap, on)         → the darkened ember look for raided buildings
//
// A recipe is either a list of Capital pieces (`parts`) OR a tile modelled in Blender (`glb`: one file, or several looks — the seed picks one),
// exported by scripts/adventure/export_tiles.mjs to public/models/tiles/. See docs/BLENDER-WORKFLOW.md.

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { CapitalConfig as CFG } from '../config/capital.js'
import { HEX_SIZE } from './hexGrid.js'
import { expandRecipe } from './hexRecipes.js'
import { addGlowTo } from './nightLights.js'
import { fixGltfScene } from './depthFix.js'

let ready = null
let lib = null      // piece name (no SM_) → { geo, m (node matrix), tex }
let recipes = null  // hex_recipes.json
let mats = []       // [_, pal1, pal2, pal3]
let burnMats = []
let nightK = 0

const TILES_DIR = '/models/tiles'
const PALETTE_RX = /^PolygonSamurai_Tex_0([1-4])\.(png|jpg)$/
let tileFiles = new Map() // glb file → [{ geo, m (node matrix), pair: { lit, burn }, tex }]
const tileTex = new Map() // texture file → THREE.Texture
const tileMats = new Map() // material key → { lit, burn }

const tex = (url) => new Promise((resolve, reject) => new THREE.TextureLoader().load(url, (t) => {
  t.colorSpace = THREE.SRGBColorSpace; t.flipY = false
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.generateMipmaps = false
  resolve(t)
}, undefined, reject))

// Extra pieces (leafy bamboo, grass, flowers, bushes, gongs…) exported from the pack's own FBX files.
// Non-fatal: if the file is missing the field simply lacks those pieces. Adds to `into` (name → { geo, m, tex }).
export async function loadExtras(base, loader, into) {
  try {
    const [gltf, side] = await Promise.all([
      loader.loadAsync(`${base}/hex_extras.glb`),
      fetch(`${base}/hex_extras.json`).then((r) => (r.ok ? r.json() : {})),
    ])
    for (const [name, info] of Object.entries(side)) {
      let mesh = null
      gltf.scene.getObjectByName(`SM_${name}`)?.traverse((o) => { if (!mesh && o.isMesh) mesh = o })
      if (!mesh) continue
      mesh.updateWorldMatrix(true, false)
      into.set(name, { geo: mesh.geometry, m: mesh.matrixWorld.clone(), tex: info.tex ?? 1 })
    }
  } catch (err) { console.warn('[synty] extra pieces unavailable:', err.message || err) }
}

export function loadSynty() {
  if (ready) return ready
  const base = CFG.dir
  const loader = new GLTFLoader(); loader.setMeshoptDecoder(MeshoptDecoder)
  ready = Promise.all([
    loader.loadAsync(`${base}/${CFG.piecesFile}`),
    fetch(`${base}/${CFG.layoutFile}`).then((r) => { if (!r.ok) throw new Error(`layout ${r.status}`); return r.json() }),
    fetch(`${base}/hex_recipes.json`).then((r) => { if (!r.ok) throw new Error(`recipes ${r.status}`); return r.json() }),
    Promise.all([1, 2, 3].map((n) => tex(`${base}/PolygonSamurai_Tex_0${n}.png`))),
    tex(`${base}/${CFG.emission}`),
  ]).then(async ([gltf, layout, rec, pals, emi]) => {
    fixGltfScene(gltf.scene) // faces the pack draws twice on the same plane would flicker (z-fighting): separate them by a hair
    lib = new Map()
    for (const piece of layout.pieces) {
      const name = piece.name.replace(/^SM_/, '')
      if (lib.has(name)) continue
      let mesh = null
      gltf.scene.getObjectByName(piece.id)?.traverse((o) => { if (!mesh && o.isMesh) mesh = o })
      if (!mesh) continue
      mesh.updateWorldMatrix(true, false)
      lib.set(name, { geo: mesh.geometry, m: mesh.matrixWorld.clone(), tex: piece.tex })
    }
    recipes = rec
    await loadExtras(base, loader, lib)
    pals.forEach((map, i) => {
      const n = i + 1
      mats[n] = new THREE.MeshLambertMaterial({ map, emissive: 0xffffff, emissiveMap: emi, emissiveIntensity: 0, side: THREE.DoubleSide })
      mats[n].color.setScalar(CFG.brightness)
      burnMats[n] = new THREE.MeshLambertMaterial({ map, emissive: 0xff3d0a, emissiveMap: map, emissiveIntensity: 0.4, side: THREE.DoubleSide })
      burnMats[n].color.setScalar(0.09) // charred: near-black, with the palette's lighter swatches glowing like embers
    })
    await loadTileFiles(loader)
    return true
  })
  return ready
}

// ---- tiles modelled in Blender ------------------------------------------------------------------
const hash32 = (str) => { let h = 2166136261; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) } return h >>> 0 }
const recipeFiles = (r) => (r?.glb ? (Array.isArray(r.glb) ? r.glb : [r.glb]) : [])

function tileTexture(file) {
  let t = tileTex.get(file)
  if (!t) {
    t = new THREE.TextureLoader().load(`${TILES_DIR}/tex/${file}`)
    t.colorSpace = THREE.SRGBColorSpace; t.flipY = false; t.anisotropy = 4
    tileTex.set(file, t)
  }
  return t
}

// The look of one material of a Blender tile: the Capital palettes reuse the shared materials (so they get the same night glow);
// anything else is a lifted Lambert from its texture (extras.mk_tex) or plain colour, with a charred twin for burnt buildings.
function tilePair(src, hasVertexColors) {
  const file = src.userData?.mk_tex
  const key = `${file ?? src.color.getHexString()}|${src.userData?.mk_alpha ?? ''}|${hasVertexColors ? 'v' : ''}|${src.userData?.tint ?? ''}`
  let pair = tileMats.get(key)
  if (pair) return pair
  const pal = file && PALETTE_RX.exec(file)
  if (pal) pair = { lit: mats[+pal[1]], burn: burnMats[+pal[1]] }
  else {
    const map = file ? tileTexture(file) : null
    const cut = src.userData?.mk_alpha === 'mask'
    const lit = new THREE.MeshLambertMaterial({ map, side: THREE.DoubleSide, vertexColors: hasVertexColors, alphaTest: cut ? 0.5 : 0 })
    const burn = new THREE.MeshLambertMaterial({ map, side: THREE.DoubleSide, vertexColors: hasVertexColors, alphaTest: cut ? 0.5 : 0, emissive: 0xff3d0a, emissiveMap: map, emissiveIntensity: 0.4 })
    if (map) { lit.color.setScalar(CFG.brightness); burn.color.setScalar(0.09) }
    const tint = src.userData?.tint
    if (map && Array.isArray(tint)) lit.color.multiply(new THREE.Color(tint[0], tint[1], tint[2])) // (a per-material colour nudge, as the lands do — see slot_map.json)
    else { lit.color.copy(src.color).multiplyScalar(CFG.brightness); burn.color.copy(src.color).multiplyScalar(0.09); burn.emissiveMap = null; burn.emissive.setHex(0x1a0600) }
    pair = { lit, burn }
  }
  tileMats.set(key, pair)
  return pair
}

async function loadTileFiles(loader) {
  tileFiles = new Map()
  const wanted = new Set()
  for (const r of Object.values(recipes.recipes)) for (const f of recipeFiles(r)) wanted.add(f)
  await Promise.all([...wanted].map(async (file) => {
    try {
      const gltf = await loader.loadAsync(`${TILES_DIR}/${file}`)
      gltf.scene.updateMatrixWorld(true)
      const parts = []
      gltf.scene.traverse((o) => {
        if (!o.isMesh) return
        const mat = Array.isArray(o.material) ? o.material[0] : o.material
        parts.push({ geo: o.geometry, m: o.matrixWorld.clone(), pair: tilePair(mat, !!o.geometry.attributes.color) })
      })
      if (parts.length) tileFiles.set(file, parts)
    } catch (err) { console.warn(`[synty] tile ${file} unavailable:`, err.message || err) }
  }))
  // a recipe whose files all failed is dropped, so the game falls back to its classic tile
  for (const [id, r] of Object.entries(recipes.recipes)) {
    const fs = recipeFiles(r)
    if (fs.length && !fs.some((f) => tileFiles.has(f))) delete recipes.recipes[id]
  }
}
const pickTileFile = (r, seed) => {
  const have = recipeFiles(r).filter((f) => tileFiles.has(f))
  return have.length ? have[hash32(`${seed}`) % have.length] : null
}
export const syntyReady = () => !!lib && !!recipes
// A library piece { geo, m (its node matrix), tex } by name, and the shared palette material for a tex index — for modules that
// draw pieces themselves (riceField.js instances the crop rows).
export const getPiece = (name) => lib?.get(name) ?? null
export const paletteMaterial = (tex) => mats[tex] ?? mats[1] ?? null
export const hasRecipe = (id) => !!recipes?.recipes?.[id]
export const getRecipe = (id) => recipes?.recipes?.[id] ?? null
const K = () => HEX_SIZE / (recipes?.hexRadiusM ?? 6.83) // source metres → world units

export function setSyntyNight(n) {
  nightK = Math.max(0, Math.min(1, n)) * 1.6
  for (const m of mats) if (m) m.emissiveIntensity = nightK
  const pulse = 0.38 + 0.16 * Math.sin(performance.now() * 0.004) // burnt buildings smoulder and breathe
  for (const m of burnMats) if (m) m.emissiveIntensity = pulse
}

// The pieces that really are light sources at night: a glow is attached to each (see nightLights.addGlowTo), so it sits ON the lantern
// and follows the building. `at` = how far up the piece the flame / lamp is (0 = foot, 1 = top); `size` = the halo's width in
// WORLD units (small: a lantern's halo, not a street light); the rest is the glow's colour and candle flicker.
const LIGHT_PIECES = [
  { rx: /Prop_Stone_Lantern/, at: 0.66, size: 0.17, intensity: 0.7, color: '#ffb25a', flicker: 0.12 },
  { rx: /Prop_Zen_Lantern/, at: 0.6, size: 0.15, intensity: 0.65, color: '#ffb866', flicker: 0.12 },
  { rx: /Prop_Paper_Lantern/, at: 0.5, size: 0.19, intensity: 0.75, color: '#ff9a48', flicker: 0.1 },
  { rx: /Prop_StoneBrazier/, at: 0.9, size: 0.22, intensity: 0.8, color: '#ff7a2a', flicker: 0.25 },
]
const _box = new THREE.Box3(), _p = new THREE.Vector3()
function pieceBounds(e) { // the piece's box in the frame of the group it hangs in (its own matrix applied)
  if (!e.bounds) {
    e.geo.computeBoundingBox()
    const b = e.geo.boundingBox, out = new THREE.Box3()
    for (const x of [b.min.x, b.max.x]) for (const y of [b.min.y, b.max.y]) for (const z of [b.min.z, b.max.z]) out.expandByPoint(_p.set(x, y, z).applyMatrix4(e.m))
    e.bounds = out
  }
  return _box.copy(e.bounds)
}

// One building / tile. `seed` (the tile key) makes scattered parts unique to that hex.
// opts.slabColor overrides the ground slab colour (the rice paddy follows the season).
export function buildRecipeGroup(id, seed, opts = {}) {
  const r = recipes?.recipes?.[id]
  if (!r || !lib) return null
  const wrap = new THREE.Group()
  wrap.userData.synty = id
  const rs = r.s ?? 1
  for (const part of expandRecipe(r, seed)) {
    if (part.p === '@hex') {
      const R = (recipes.hexRadiusM) * 0.985
      const mat = new THREE.MeshLambertMaterial({ color: opts.slabColor ?? part.c ?? '#79a04b' })
      const slab = new THREE.Mesh(new THREE.CylinderGeometry(R, R, 0.5, 6), mat)
      slab.position.y = -0.25
      slab.userData.slab = true
      wrap.userData.slabMat = mat
      wrap.add(slab)
      continue
    }
    const e = lib.get(part.p)
    if (!e) continue
    const mesh = new THREE.Mesh(e.geo, mats[e.tex] ?? mats[1])
    mesh.matrixAutoUpdate = false
    mesh.matrix.copy(e.m)
    mesh.userData.tex = e.tex
    const g = new THREE.Group()
    g.add(mesh)
    const k = (part.s ?? 1) * rs
    g.scale.set(k * (part.w ?? 1), k * (part.h ?? 1), k * (part.w ?? 1)) // w = width factor, h = height factor
    g.rotation.y = ((part.r ?? 0) * Math.PI) / 180
    g.position.set((part.x ?? 0) * rs, (part.y ?? 0) * rs, (part.z ?? 0) * rs)
    g.userData.piece = part.p // (so a caller can find e.g. the crop rows — scene/riceField.js draws those itself)
    wrap.add(g)
    const light = LIGHT_PIECES.find((l) => l.rx.test(part.p))
    if (light) { // a real lantern / brazier: its glow sits on the lamp, in the piece's own frame
      const b = pieceBounds(e)
      const local = light.size / (k * K()) // (the group and the wrap are scaled down: ask for the size in their units)
      const node = addGlowTo(g, (b.min.x + b.max.x) / 2, b.min.y + light.at * (b.max.y - b.min.y), (b.min.z + b.max.z) / 2, { size: local, intensity: light.intensity, color: light.color, flicker: light.flicker })
      ;(wrap.userData.glowNodes ??= []).push(node)
    }
  }
  const file = pickTileFile(r, seed)
  if (file) {
    for (const part of tileFiles.get(file)) {
      const mesh = new THREE.Mesh(part.geo, part.pair.lit)
      mesh.matrixAutoUpdate = false
      mesh.matrix.copy(part.m)
      if (rs !== 1) mesh.matrix.premultiply(new THREE.Matrix4().makeScale(rs, rs, rs))
      mesh.userData.pair = part.pair
      wrap.add(mesh)
    }
  }
  wrap.scale.setScalar(K())
  return wrap
}

// Darkened, ember-tinted look for a raided building (and back).
const SOOT = 0x14100c
export function setBurned(wrap, on) {
  if (!wrap?.userData?.synty) return
  wrap.traverse((o) => {
    if (!o.isMesh) return
    const pair = o.userData.pair // a tile modelled in Blender
    if (pair) { o.material = on ? pair.burn : pair.lit; return }
    if (!o.userData.tex) return
    o.material = on ? (burnMats[o.userData.tex] ?? burnMats[1]) : (mats[o.userData.tex] ?? mats[1])
  })
  for (const n of wrap.userData.glowNodes ?? []) n.userData.off = on // a burnt building's lanterns are out
  const slab = wrap.userData.slabMat // the ground under it is scorched black too, then restored
  if (slab) {
    if (on) { if (wrap.userData.slabBefore == null) wrap.userData.slabBefore = slab.color.getHex(); slab.color.setHex(SOOT) }
    else if (wrap.userData.slabBefore != null) { slab.color.setHex(wrap.userData.slabBefore); wrap.userData.slabBefore = null }
  }
}

// Undeveloped-land cover for plain tiles: every tile gets its own scatter (seeded by its key), drawn as
// one InstancedMesh per piece type for the whole field. tiles: [{ key, x, z }] in world units.
export function buildGroundCover(tiles, recipeId = 'GROUND') {
  const r = recipes?.recipes?.[recipeId]
  if (!r || !lib) return null
  const group = new THREE.Group(); group.name = 'synty-ground-cover'
  const k = K(), rs = r.s ?? 1
  const byPiece = new Map()
  const m = new THREE.Matrix4(), local = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), p = new THREE.Vector3()
  const up = new THREE.Vector3(0, 1, 0)
  const byTilePart = new Map() // Blender-modelled cover: one InstancedMesh per (file, part)
  for (const t of tiles) {
    const file = pickTileFile(r, t.key)
    if (file) {
      q.setFromAxisAngle(up, (hash32(`${t.key}#rot`) % 6) * (Math.PI / 3)) // a plain tile may be turned in 60° steps: same hex, fresh look
      s.setScalar(k * rs)
      p.set(t.x, t.y ?? 0, t.z)
      local.compose(p, q, s)
      tileFiles.get(file).forEach((part, i) => {
        const id = `${file}#${i}`
        let e = byTilePart.get(id); if (!e) byTilePart.set(id, (e = { part, list: [] }))
        e.list.push(m.multiplyMatrices(local, part.m).clone())
      })
      continue
    }
    for (const part of expandRecipe(r, t.key)) {
      if (part.p === '@hex') continue
      const e = lib.get(part.p); if (!e) continue
      q.setFromAxisAngle(up, ((part.r ?? 0) * Math.PI) / 180)
      const sc = (part.s ?? 1) * rs * k
      s.set(sc * (part.w ?? 1), sc, sc * (part.w ?? 1))
      p.set(t.x + (part.x ?? 0) * rs * k, (t.y ?? 0) + (part.y ?? 0) * rs * k, t.z + (part.z ?? 0) * rs * k)
      local.compose(p, q, s)
      m.multiplyMatrices(local, e.m)
      let list = byPiece.get(part.p); if (!list) byPiece.set(part.p, (list = []))
      list.push(m.clone())
    }
  }
  for (const [name, list] of byPiece) {
    const e = lib.get(name)
    const im = new THREE.InstancedMesh(e.geo, mats[e.tex] ?? mats[1], list.length)
    list.forEach((mm, i) => im.setMatrixAt(i, mm))
    im.instanceMatrix.needsUpdate = true
    im.computeBoundingSphere()
    im.raycast = () => {} // never block click-to-move
    group.add(im)
  }
  for (const { part, list } of byTilePart.values()) {
    const im = new THREE.InstancedMesh(part.geo, part.pair.lit, list.length)
    list.forEach((mm, i) => im.setMatrixAt(i, mm))
    im.instanceMatrix.needsUpdate = true
    im.computeBoundingSphere()
    im.raycast = () => {}
    group.add(im)
  }
  return group
}

// Free-form scenery from Capital pieces, instanced per piece type. items: [{ p, x, y, z, ry (rad), sx, sy, sz }]
// in WORLD units (x/y/z) with sx/sy/sz as scales on the piece's native metres → world (pass SCENERY_K * factor).
export const SCENERY_K = () => HEX_SIZE / (recipes?.hexRadiusM ?? 6.83)
export function buildScenery(items) {
  if (!lib) return null
  const group = new THREE.Group(); group.name = 'synty-scenery'
  const byPiece = new Map()
  const m = new THREE.Matrix4(), local = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), p = new THREE.Vector3()
  const up = new THREE.Vector3(0, 1, 0)
  for (const it of items) {
    const e = lib.get(it.p); if (!e) continue
    q.setFromAxisAngle(up, it.ry ?? 0)
    s.set(it.sx, it.sy ?? it.sx, it.sz ?? it.sx)
    p.set(it.x, it.y, it.z)
    local.compose(p, q, s)
    m.multiplyMatrices(local, e.m)
    let list = byPiece.get(it.p); if (!list) byPiece.set(it.p, (list = []))
    list.push(m.clone())
  }
  for (const [name, list] of byPiece) {
    const e = lib.get(name)
    const im = new THREE.InstancedMesh(e.geo, mats[e.tex] ?? mats[1], list.length)
    list.forEach((mm, i) => im.setMatrixAt(i, mm))
    im.instanceMatrix.needsUpdate = true
    im.computeBoundingSphere()
    im.raycast = () => {}
    group.add(im)
  }
  return group
}
